import classNames from 'classnames';
import styles from './Form.module.scss';
import { Heading } from 'ui/components/1-atoms/Heading/Heading';
import { Picture } from 'ui/components/1-atoms/Media/Picture/Picture';
import { Container } from 'ui/components/4-habitats/Container/Container';
import { TopLevelGrid } from 'ui/components/4-habitats/TopLevelGrid/TopLevelGrid';
import { TopLevelGridCell } from 'ui/components/4-habitats/TopLevelGrid/TopLevelGridCell';
import { Grid } from 'ui/components/4-habitats/Grid/Grid';
import { GridCell } from 'ui/components/4-habitats/Grid/GridCell';
import ColorOptions, { GridLayoutTypeOptions } from 'helpers/types';
import { OrttoForm } from 'ui/components/2-molecules/OrttoForm/OrttoForm';
import { Kicker, KickerProps } from 'ui/components/1-atoms/Kicker/Kicker';
import { ActiveCampaignForm } from 'ui/components/2-molecules/ActiveCampaignForm/ActiveCampaignForm';

export interface FormProps {
	bgColor?: ColorOptions;
	kicker: KickerProps;
	heading: string;
	image?: {
		url: string;
		focalPoint?: { top: number; left: number };
		properties?: {
			altText?: string;
			umbracoExtension?: string;
		};
	};
	formHtmlCode: string;
	showImage?: boolean;
	layoutType?: GridLayoutTypeOptions;
	displayButtonRight?: boolean;
	selectAllLabel?: string;
	placeholderLabel?: string;
	inputFullWidth?: boolean;
	variant?: 'Ortto' | 'ActiveCampaign' | 'HubSpot';
	buttonStyle?: 'primary' | 'secondary';
}

export const Form: React.FC<FormProps> = ({
	bgColor = ColorOptions.White,
	kicker,
	heading,
	image,
	formHtmlCode,
	showImage = false,
	layoutType,
	displayButtonRight,
	selectAllLabel,
	placeholderLabel,
	inputFullWidth,
	variant,
	buttonStyle = 'secondary',
}) => {
	return (
		<div className={classNames(styles.Form, `u-color___${bgColor}`)}>
			<Container width="Standard">
				<TopLevelGrid container wrap>
					<TopLevelGridCell desktopWidth="16col" mobileWidth="100">
						{kicker && <Kicker className={styles.Form_trumpetHeading} {...kicker}></Kicker>}

						{heading && (
							<Heading className={styles.Form_heading} headingLevel="h2" style="xl">
								{heading}
							</Heading>
						)}
					</TopLevelGridCell>

					{showImage && (
						<TopLevelGridCell desktopWidth="22col" mobileWidth="100" desktopOffset="0col">
							<Grid container wrap largeGutter>
								{layoutType === 'oneThirdImage' ? (
									<>
										<GridCell desktopWidth="33" mobileWidth="100">
											{image && (
												<Picture
													className={styles.Form_image}
													url={image.url}
													focalPoint={image.focalPoint}
													altText={image.properties.altText}
													sizes="(max-width: 768px) 100vw, (max-width: 1728px) 66vw, 1150px"
													fileType={image.properties.umbracoExtension}
												/>
											)}
										</GridCell>
										{variant !== 'ActiveCampaign' && (
											<GridCell desktopWidth="66" mobileWidth="100" className={classNames('ortto')}>
												<OrttoForm
													className="two-thirds"
													formHtmlCode={formHtmlCode}
													displayButtonRight={displayButtonRight}
													selectAllLabel={selectAllLabel}
													inputFullWidth={inputFullWidth}
													placeholderLabel={placeholderLabel}
													buttonStyle={buttonStyle}
													bgColor={bgColor}
											/>
											</GridCell>
										)}
										{variant === 'ActiveCampaign' && (
											<GridCell desktopWidth="66" mobileWidth="100" className={classNames('activecampaign')}>
												<ActiveCampaignForm
													className="two-thirds"
													formHtmlCode={formHtmlCode}
													displayButtonRight={displayButtonRight}
													selectAllLabel={selectAllLabel}
													inputFullWidth={inputFullWidth}
													placeholderLabel={placeholderLabel}
												/>
											</GridCell>
										)}
									</>
								) : layoutType === 'halfImage' ? (
									<>
										<GridCell desktopWidth="50" mobileWidth="100">
											{image && (
												<Picture
													className={styles.Form_image}
													url={image.url}
													altText={image.properties.altText}
													sizes="(max-width: 768px) 100vw, (max-width: 1728px) 66vw, 1150px"
													fileType={image.properties.umbracoExtension}
												/>
											)}
										</GridCell>
										<GridCell desktopWidth="50" mobileWidth="100">
											{variant !== 'ActiveCampaign' && (
												<OrttoForm
													className="half"
													formHtmlCode={formHtmlCode}
													displayButtonRight={displayButtonRight}
													selectAllLabel={selectAllLabel}
													inputFullWidth={inputFullWidth}
													placeholderLabel={placeholderLabel}
													buttonStyle={buttonStyle}
													bgColor={bgColor}
											/>
											)}
											{variant === 'ActiveCampaign' && (
												<ActiveCampaignForm
													className="half"
													formHtmlCode={formHtmlCode}
													displayButtonRight={displayButtonRight}
													selectAllLabel={selectAllLabel}
													inputFullWidth={inputFullWidth}
													placeholderLabel={placeholderLabel}
												/>
											)}
										</GridCell>
									</>
								) : (
									<>
										<GridCell desktopWidth="66" mobileWidth="100">
											{image && (
												<Picture
													className={styles.Form_image}
													url={image.url}
													altText={image.properties.altText}
													sizes="(max-width: 768px) 100vw, (max-width: 1728px) 66vw, 1150px"
													fileType={image.properties.umbracoExtension}
												/>
											)}
										</GridCell>
										<GridCell desktopWidth="33" mobileWidth="100">
											{variant !== 'ActiveCampaign' && (
												<OrttoForm
													formHtmlCode={formHtmlCode}
													displayButtonRight={displayButtonRight}
													selectAllLabel={selectAllLabel}
													inputFullWidth={inputFullWidth}
													placeholderLabel={placeholderLabel}
													buttonStyle={buttonStyle}
													bgColor={bgColor}
											/>
											)}
											{variant === 'ActiveCampaign' && (
												<ActiveCampaignForm
													formHtmlCode={formHtmlCode}
													displayButtonRight={displayButtonRight}
													selectAllLabel={selectAllLabel}
													inputFullWidth={inputFullWidth}
													placeholderLabel={placeholderLabel}
												/>
											)}
										</GridCell>
									</>
								)}
							</Grid>
						</TopLevelGridCell>
					)}

					{!showImage && (
						<TopLevelGridCell desktopWidth="24col" mobileWidth="100" desktopOffset="0col">
							<Grid container wrap>
								<GridCell desktopWidth="100" mobileWidth="100">
									{variant !== 'ActiveCampaign' && (
										<OrttoForm
											className="ortto-noImage"
											formHtmlCode={formHtmlCode}
											displayButtonRight={displayButtonRight}
											selectAllLabel={selectAllLabel}
											inputFullWidth={inputFullWidth}
											placeholderLabel={placeholderLabel}
											buttonStyle={buttonStyle}
									/>
									)}
									{variant === 'ActiveCampaign' && (
										<ActiveCampaignForm
											className="activecampaign-noImage"
											formHtmlCode={formHtmlCode}
											displayButtonRight={displayButtonRight}
											selectAllLabel={selectAllLabel}
											inputFullWidth={inputFullWidth}
											placeholderLabel={placeholderLabel}
										/>
									)}
								</GridCell>
							</Grid>
						</TopLevelGridCell>
					)}
				</TopLevelGrid>
			</Container>
		</div>
	);
};

